'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { RotateCcw, RefreshCw, TrendingUp, TrendingDown, Award } from 'lucide-react';
import type { TestResult } from '@/data/localScores';
import { getLanguageConfig } from '@/data/wordLists';
import AccuracyDonut from '@/app/results/components/AccuracyDonut';
import WpmTimelineChart from '@/app/results/components/WpmTimelineChart';

interface ResultPanelProps {
  result: TestResult;
  isNewBest: boolean;
  prevBest: number | null;
  onRetry: () => void;
  onNewTest: () => void;
}

function rating(wpm: number): string {
  if (wpm >= 120) return 'Expert';
  if (wpm >= 80) return 'Above average';
  if (wpm >= 50) return 'Average';
  return 'Keep practicing';
}

/** Counts a number up from 0 to `target` once on mount (respects reduced motion). */
function useCountUp(target: number, duration = 900): number {
  const [value, setValue] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || target === 0) {
      setValue(target);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      // easeOutCubic for a satisfying settle
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [target, duration]);

  return value;
}

export default function ResultPanel({ result, isNewBest, prevBest, onRetry, onNewTest }: ResultPanelProps) {
  const lang = getLanguageConfig(result.language);
  const animatedWpm = useCountUp(result.wpm);

  const delta = prevBest !== null ? result.wpm - prevBest : null;

  const miniStats = [
    { label: 'CPM', value: result.cpm, sub: 'characters per minute' },
    { label: 'Keystrokes', value: result.keystrokes, sub: 'total keypresses' },
    { label: 'Error rate', value: `${result.errorRate.toFixed(1)}%`, sub: 'errors per 100 keys' },
  ];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      {isNewBest && (
        <div className="new-personal-best flex items-center justify-center gap-2.5 py-2.5 px-4 animate-celebrate">
          <span className="eyebrow" style={{ color: 'var(--foreground)' }}>New personal best</span>
          <span className="font-mono text-sm text-muted-foreground">
            {result.wpm} WPM
          </span>
        </div>
      )}

      {/* Result sheet — the figure and the accuracy read as one page */}
      <div className="card-elevated overflow-hidden">
        <div
          className="flex items-center justify-between gap-3 px-5 py-2.5"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <span className="eyebrow">Result</span>
          <span className="text-xs text-faint">
            {lang.flag} {lang.label} · {result.duration} second test
          </span>
        </div>

        <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
          {/* The figure */}
          <div className="px-6 py-9 sm:py-12 flex flex-col items-center justify-center text-center">
            <div
              className="font-serif stat-value leading-none text-foreground"
              style={{ fontSize: 'clamp(4.5rem, 12vw, 6.5rem)', fontWeight: 600, letterSpacing: '-0.03em' }}
            >
              {animatedWpm}
            </div>
            <div className="eyebrow mt-3">words per minute</div>

            <div
              className="mt-5 pt-4 flex flex-col items-center gap-2"
              style={{ borderTop: '1px solid var(--border)', minWidth: 200 }}
            >
              <span className="text-sm font-medium text-foreground">{rating(result.wpm)}</span>

              {delta !== null && (
                <span className="text-xs text-faint flex items-center gap-1.5">
                  Best{' '}
                  <span className="font-mono text-muted-foreground">
                    {Math.max(prevBest!, result.wpm)}
                  </span>
                  {delta > 0 ? (
                    <span className="pb-delta pb-delta-up">
                      <TrendingUp size={11} /> +{delta}
                    </span>
                  ) : delta < 0 ? (
                    <span className="pb-delta pb-delta-down">
                      <TrendingDown size={11} /> {delta}
                    </span>
                  ) : (
                    <span className="pb-delta pb-delta-tie">tied</span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Accuracy, divided by a hairline rather than a gap */}
          <div className="lg:rule-left" style={{ borderTop: '1px solid var(--border)' }}>
            <AccuracyDonut
              accuracy={result.accuracy}
              correctWords={result.correctWords}
              wrongWords={result.wrongWords}
              bare
            />
          </div>
        </div>
      </div>

      {/* WPM over time */}
      <WpmTimelineChart timeline={result.wpmTimeline} duration={result.duration} />

      {/* Secondary figures */}
      <div className="grid grid-cols-3 gap-3">
        {miniStats.map((c) => (
          <div key={c.label} className="stat-card">
            <div className="stat-card-label">{c.label}</div>
            <div className="stat-card-value stat-value">{c.value}</div>
            <div className="stat-card-sub">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-2">
        <button
          onClick={onRetry}
          className="btn-primary flex items-center justify-center gap-2 px-6 py-2.5 text-sm w-full sm:w-auto"
        >
          <RotateCcw size={16} />
          Retry same test
        </button>
        <button
          onClick={onNewTest}
          className="btn-secondary flex items-center justify-center gap-2 px-6 py-2.5 text-sm w-full sm:w-auto"
        >
          <RefreshCw size={16} />
          New random test
        </button>
      </div>

      <div className="flex justify-center">
        <Link
          href="/certificate"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Award size={14} />
          Claim your typing certificate
        </Link>
      </div>
    </div>
  );
}
